import React, { useMemo } from 'react';
import { Tour, TourProps } from 'antd';
import useUserPreference from '../hooks/useUserPreference';
import styles from './Components.module.css';
import ReactGA from 'react-ga';

import list from '../studio/assets/onboarding_list_icon.png';
import camera from '../studio/assets/camera_icon.png';
import social from '../studio/assets/social_icon.png';
import link from '../studio/assets/clip_icon.png';

import slide1 from '../studio/assets/newBrandingIllustrations/giide-illo-08.png';
import slide2 from '../studio/assets/newBrandingIllustrations/giide-illo-04.png';
import slide3 from '../studio/assets/newBrandingIllustrations/giide-illo-09.png';
import { colors } from '../theme';

const slides = [
  {
    prompt: 'Talk about your dream dinner guest.',
    background: colors.teal1_20,
    image: slide1,
    altText: 'woman sitting on couch with laptop',
    items: [
      {
        text: 'Link to their social profiles(s)',
        icon: social,
      },
      {
        text: 'List the reason(s) you chose them',
        icon: list,
      },
      {
        text: 'Attach a picture',
        icon: camera,
      },
      {
        text: 'Link to their wiki page',
        icon: link,
      },
    ],
  },
  {
    prompt: 'Talk about your favorite food.',
    background: colors.yellow1_20,
    image: slide2,
    altText: 'two people sitting at table outside drinking wine',
    items: [
      {
        text: 'Link to a recipe.',
        icon: link,
      },
      {
        text: 'Attach some pictures',
        icon: camera,
      },
      {
        text: "Explain why it's the best",
        icon: list,
      },
      {
        text: 'Link to a restaurant that serves it',
        icon: link,
      },
    ],
  },
  {
    prompt: 'Talk about your favorite vacation spot.',
    background: colors.white3,
    image: slide3,
    altText: 'hiker looking back at two hot air balloons',
    items: [
      {
        text: 'Link to a map',
        icon: link,
      },
      {
        text: 'Attach some pictures',
        icon: camera,
      },
      {
        text: 'List your favorite things to do there',
        icon: list,
      },
      {
        text: 'Link to the top attraction(s)',
        icon: link,
      },
    ],
  },
];

const OnboardingModal: React.FC = () => {
  const { preference, updatePreference } = useUserPreference('firstLoginVideo');
  const [isModalVisible, setIsModalVisible] = React.useState(!preference);

  const handleCancel = () => {
    setIsModalVisible(false);
    updatePreference(true);
    ReactGA.event({
      category: 'Event',
      action: 'Onboarding Modal Viewed',
    });
  };

  const tourSteps: TourProps['steps'] = useMemo(() => {
    const steps = [
      {
        title: <div className={styles.onboardingTitle}>Welcome to Giide!</div>,
        nextButtonProps: { children: 'Explore Ideas' },
        description: (
          <div className={styles.onboardingSlide}>
            <div className={styles.onboardingLeft}>
              <div className={styles.welcome}>
                <div>
                  Let's get you started on crafting your first giide in three
                  simple steps:
                </div>
                <div className={styles.numberedContainer}>
                  <span>1</span>
                  <div>
                    <h3>Add Audio</h3>
                    <p>
                      Record yourself here in studio, or upload audio you
                      already have. We'll then transcribe it.
                    </p>
                  </div>
                </div>
                <div className={styles.numberedContainer}>
                  <span>2</span>
                  <div>
                    <h3>Create Feed</h3>
                    <p>
                      Click on words in the transcription to attach links,
                      pictures, and more to your audio, resulting in a visual
                      feed.
                    </p>
                  </div>
                </div>
                <div className={styles.numberedContainer}>
                  <span>3</span>
                  <div>
                    <h3>Publish</h3>
                    <p>
                      Share your giide with the world via a URL or QR code, or
                      embed it on your own platform.
                    </p>
                  </div>
                </div>
                <div className={styles.bottomText}>
                  <p>
                    Need inspiration? We'll provide creative prompts in the
                    following steps.
                  </p>
                </div>
              </div>
            </div>
            <iframe
              title="welcome to giide"
              src={`https://${process.env.REACT_APP_EMBED_URL}/giide/v2/giide-19693769-b26d-4fba-a3c1-ca187c61eb23`}
              frameBorder="0"
              className={styles.onboardingGiide}
            />
          </div>
        ),
      },
    ];

    const additionalSteps = slides.map((slide, i) => ({
      title: <div className={styles.onboardingTitle}>{slide.prompt}..</div>,
      nextButtonProps:
        i === slides.length - 1 ? { children: 'Get Started' } : undefined,
      description: (
        <div className={styles.onboardingSlide} key={`onboarding-slide-${i}`}>
          <div className={styles.onboardingLeft}>
            <div
              className={styles.prompt}
              style={{ backgroundColor: slide.background }}
            >
              <p>Then in your feed you could:</p>
            </div>
            {slide.items.map((item, i) => (
              <div className={styles.slideItem} key={`item-slide-${i}`}>
                <img src={item.icon} alt="slide-icon" />
                <p>{item.text}</p>
              </div>
            ))}
          </div>
          <div className={styles.onboardingRight}>
            <img
              src={slide.image}
              alt={slide.altText}
              className={styles.onboardingImage}
            />
          </div>
        </div>
      ),
    }));

    return [...steps, ...additionalSteps];
  }, []);

  return (
    <div>
      <Tour
        onClose={handleCancel}
        open={isModalVisible}
        placement={'top'}
        steps={tourSteps}
      />
    </div>
  );
};

export default OnboardingModal;
